import { Box } from "@mui/material"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"

import useTranslate, { Namespaces } from "../../../../hooks/use-translate"
import { s } from "../../../../utilities/common"
import { theme } from "../../../../utilities/theme"
import { useProgress } from "../../use-progress"

export const ProgressRightButton = ({
  focusKey,
  type = "chapter",
}: {
  focusKey?: string
  type: "chapter" | "title" | "progress"
}) => {
  const t = useTranslate(Namespaces.Common)
  const { changeChapter } = useProgress()
  const { focused, focusSelf, ref } = useFocusable({
    focusKey: focusKey,
    onEnterPress: () => changeChapter("right"),
  })

  return (
    <Box
      tabIndex={0}
      aria-label={t("player__button_forward")}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={() => changeChapter("right")}
      ref={ref}
    >
      {type === "chapter" ? (
        <svg
          width={s("72px")}
          height={s("72px")}
          viewBox="32 24 72 72"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="68"
            cy="60"
            r="36"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M51.9893 72.2012C50.7295 72.2012 49.6602 71.249 49.6602 69.5205V52.4111C49.6602 50.668 50.7148 49.7158 51.9746 49.7158C52.6045 49.7158 53.1465 49.8916 53.7617 50.2578L67.9854 58.6074C68.835 59.1055 69.3623 59.6768 69.5527 60.3799V52.4111C69.5527 50.668 70.5928 49.7158 71.8525 49.7158C72.4824 49.7158 73.0244 49.8916 73.6396 50.2578L87.8633 58.6074C88.9473 59.2373 89.5039 59.9697 89.5039 60.9512C89.5039 61.9326 88.9326 62.6943 87.8633 63.3096L73.6396 71.6592C73.0391 72.0254 72.4971 72.2012 71.8672 72.2012C70.6074 72.2012 69.5527 71.249 69.5527 69.5205V61.5225C69.3623 62.2402 68.835 62.8262 67.9854 63.3096L53.7617 71.6592C53.1465 72.0254 52.6191 72.2012 51.9893 72.2012Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        </svg>
      ) : type === "title" ? (
        <svg
          width={s("72px")}
          height={s("72px")}
          viewBox="32 24 72 72"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="68"
            cy="60"
            r="36"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M56.6914 72.2451C55.4316 72.2451 54.3623 71.293 54.3623 69.5498V52.4404C54.3623 50.6973 55.417 49.7598 56.6768 49.7598C57.3066 49.7598 57.8486 49.9355 58.4639 50.2871L72.6875 58.6367C73.5371 59.1494 74.0645 59.7061 74.2402 60.4092V51.4443C74.2402 50.082 74.9434 49.3936 76.291 49.3936H79.5723C80.9199 49.3936 81.623 50.0674 81.623 51.4443V70.5605C81.623 71.9082 80.9199 72.6113 79.5723 72.6113H76.291C74.9287 72.6113 74.2402 71.9082 74.2402 70.5605V61.5518C74.0645 62.2695 73.5371 62.8555 72.6875 63.3535L58.4639 71.7031C57.8486 72.0547 57.3066 72.2451 56.6914 72.2451Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        </svg>
      ) : (
        <svg
          width={s("72")}
          height={s("72")}
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            width="32"
            height="32"
            rx="16"
            fill={focused ? "white" : theme.palette.new.playerButton}
          />
          <path
            d="M16.0042 10.9C13.1853 10.9 10.9001 13.1852 10.9001 16.0042C10.9001 18.8231 13.1853 21.1084 16.0042 21.1084C18.8232 21.1084 21.1084 18.8231 21.1084 16.0042C21.1084 14.2812 20.2547 12.7576 18.9472 11.8334"
            stroke={focused ? theme.palette.new.playerButton : "white"}
            stroke-width="1.3"
            strokeLinecap="round"
          />
          <path
            d="M15.3391 9.66358L15.3391 10.4432L15.3391 11.6126L15.3391 12.3923C15.3269 12.7699 15.7654 12.9404 16.0334 12.6724L17.3978 11.3081C17.5561 11.1497 17.5561 10.9061 17.3856 10.7356L16.0212 9.37123C15.7654 9.11541 15.3269 9.28595 15.3391 9.66358Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
          <path
            d="M15.4711 13.9746C15.6023 13.9746 15.7229 14.065 15.764 14.1855L15.7797 14.2324C15.8024 14.3262 15.7803 14.4263 15.7181 14.5029L15.684 14.5391L14.5121 15.6123H14.6527C15.3545 15.6123 15.9425 16.1901 15.9427 16.9014C15.9427 17.6128 15.3546 18.1914 14.6527 18.1914H13.8998C13.4617 18.1914 13.0708 17.9156 12.9369 17.4961L12.9213 17.4316C12.9005 17.2817 12.988 17.1366 13.1381 17.0918L13.2025 17.0762C13.3529 17.0554 13.4983 17.1439 13.5511 17.293V17.2939L13.5746 17.3496C13.6383 17.4729 13.763 17.5467 13.9086 17.5469H14.6527C15.0019 17.5469 15.2972 17.2594 15.2972 16.9014C15.297 16.5526 15.0015 16.2568 14.6527 16.2568H13.6859C13.5465 16.2568 13.4242 16.1769 13.3832 16.0537V16.0527C13.3342 15.9321 13.3642 15.7923 13.4642 15.7021L14.6361 14.6201H13.2396C13.0585 14.6201 12.9165 14.479 12.9164 14.2979C12.9164 14.1264 13.0579 13.9746 13.2396 13.9746H15.4711ZM17.8392 13.9844C18.6712 13.9845 19.3519 14.6559 19.3519 15.4883V16.6787C19.3519 17.5108 18.6713 18.1913 17.8392 18.1914C16.9976 18.1914 16.3256 17.5108 16.3256 16.6787V15.4971C16.3257 14.6557 16.9978 13.9844 17.8392 13.9844ZM17.8392 14.6201C17.3603 14.6201 16.9711 15.0183 16.9711 15.4883V16.6787C16.9711 17.1578 17.3602 17.5469 17.8392 17.5469C18.3091 17.5467 18.7063 17.1575 18.7064 16.6787V15.4883C18.7064 15.0185 18.3089 14.6203 17.8392 14.6201Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
            stroke={focused ? theme.palette.new.playerButton : "white"}
            stroke-width="0.05"
          />
        </svg>
      )}
    </Box>
  )
}
